import React, { useState, useEffect } from 'react';
import bbox from '@turf/bbox'; // Importa bbox da turf
import { PMTiles } from 'pmtiles'; // Importa PMTiles
import { Plus, Trash2 } from 'lucide-react'; // Importa icone
import styles from './AddVectorLayerDialog.module.css'; // Importa CSS Modules
import useGeoData from '../../../../store/useGeoData';
import type {
  GeoDataState,
  BBox,
  LayerBase,
  SourceSpecificationUnion,
  VectorSourceSpecification,
  GeoJSONSourceSpecification,
  GeoJSONSourceData,
} from '../../../../store/types';
// import { useFileProcessor } from '../../../../hooks/useFileProcessor'; // Rimosso perché gestiamo solo URL
import { ExtendedTextInput } from '../../../../common/inputs/TextInput/ExtendedTextInput';
import { NumberInput } from '../../../../common/inputs/NumberInput/NumberInput';
import UrlInput from '../../../../common/inputs/UrlInput/UrlInput';
import ToggleButton from '../../../../common/inputs/ToggleButton/ToggleButton'; // Per checkbox
import { SelectInput } from '../../../../common/inputs/SelectInput/SelectInput'; // Per select

// Interfaccia per la selezione multipla di source-layer
interface SourceLayerSelection {
  id: string; // Identificatore univoco per la riga
  sourceLayerName: string;
  maplibreLayerType: LayerBase['maplibreLayerType'];
}

// Definizione del payload per l'aggiunta di layer vettoriali
interface AddVectorLayerPayload {
  id: string;
  name: string;
  sourceType: 'vector' | 'geojson';
  maplibreLayerType: LayerBase['maplibreLayerType']; // Tipo di rendering MapLibre
  sourceLayer?: string; // Nome del layer sorgente specifico all'interno di un tileset vettoriale (per type: 'vector')
  sourceSpecification: VectorSourceSpecification | GeoJSONSourceSpecification;
  calculatedBoundsForZoom?: BBox; // NUOVO CAMPO OPZIONALE per i bounds pre-calcolati
}

interface AddVectorLayerDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type VectorUrlSourceType = 'GeoJSON_URL' | 'TileJSON' | 'XYZ_VectorTiles' | 'PMTiles_URL';
// type UploadMode = 'url' | 'file'; // Rimosso, solo URL

const AddVectorLayerDialog: React.FC<AddVectorLayerDialogProps> = ({ isOpen, onClose }) => {
  const [layerName, setLayerName] = useState('');
  // const [uploadMode, setUploadMode] = useState<UploadMode>('url'); // Rimosso
  const [sourceUrl, setSourceUrl] = useState('');
  const [urlSourceType, setUrlSourceType] = useState<VectorUrlSourceType>('GeoJSON_URL');
  // const { geoJsonData, error: fileError, isLoading: isFileLoading, processFile, reset: resetFileProcessor } = useFileProcessor(); // Rimosso
  // const fileInputRef = useRef<HTMLInputElement>(null); // Rimosso

  // Nuovi stati per il selettore dinamico del layer sorgente
  const [sourceLayerOptions, setSourceLayerOptions] = useState<{ value: string, label: string }[]>([{ value: '', label: 'Use a valid URL' }]);
  const [isLoadingSourceLayers, setIsLoadingSourceLayers] = useState(false);
  const [sourceLayerError, setSourceLayerError] = useState<string | null>(null);

  const [attribution, setAttribution] = useState('');
  const [sourceMinzoom, setSourceMinzoom] = useState<string | number>('');
  const [sourceMaxzoom, setSourceMaxzoom] = useState<string | number>('');

  // Stato per le selezioni multiple di source layer
  const [sourceLayerSelections, setSourceLayerSelections] = useState<SourceLayerSelection[]>([
    { id: `default-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' },
  ]);
  const [vectorBounds, setVectorBounds] = useState<string>('');
  const [vectorScheme, setVectorScheme] = useState<'xyz' | 'tms' | ''>('');
  const [vectorTilesString, setVectorTilesString] = useState<string>('');
  const [vectorTileSize, setVectorTileSize] = useState<string>('');

  const [geojsonBuffer, setGeojsonBuffer] = useState<string | number>('');
  const [geojsonTolerance, setGeojsonTolerance] = useState<string | number>('');
  const [geojsonCluster, setGeojsonCluster] = useState<boolean>(false);
  const [geojsonClusterRadius, setGeojsonClusterRadius] = useState<string | number>('50');
  const [geojsonSourceMaxzoom, setGeojsonSourceMaxzoom] = useState<string | number>('18'); // Maxzoom per il tiling della sorgente GeoJSON
  const [geojsonPromoteId, setGeojsonPromoteId] = useState<string>('');
  const [geojsonGenerateId, setGeojsonGenerateId] = useState<boolean>(false);
  const [geojsonClusterMaxZoom, setGeojsonClusterMaxZoom] = useState<string | number>('14'); // Max zoom a cui clusterizzare
  const [geojsonClusterMinPoints, setGeojsonClusterMinPoints] = useState<string | number>('2'); // Min punti per formare un cluster
  const [isLoadingGeoJson, setIsLoadingGeoJson] = useState(false); // Stato per caricamento GeoJSON
  const [isLoadingPMTilesMeta, setIsLoadingPMTilesMeta] = useState(false); // Stato per caricamento metadati PMTiles
  // NOTA: isLoadingSourceLayers è già definito sopra


  const addVectorSourceAndLayer = useGeoData((state: GeoDataState) => state.addVectorSourceAndLayer);

  // Funzioni helper per gestire sourceLayerSelections
  const addSourceLayerSelection = () => {
    setSourceLayerSelections(prev => [
      ...prev,
      { id: `new-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' },
    ]);
  };

  const removeSourceLayerSelection = (idToRemove: string) => {
    setSourceLayerSelections(prev => prev.filter(selection => selection.id !== idToRemove));
  };

  const updateSourceLayerSelection = (
    idToUpdate: string,
    field: keyof Omit<SourceLayerSelection, 'id'>,
    value: string | LayerBase['maplibreLayerType']
  ) => {
    setSourceLayerSelections(prev =>
      prev.map(selection =>
        selection.id === idToUpdate ? { ...selection, [field]: value } : selection
      )
    );
  };


  useEffect(() => {
    if (isOpen) {
      setLayerName('');
      // setUploadMode('url'); // Rimosso
      setSourceUrl('');
      setUrlSourceType('GeoJSON_URL');
      // resetFileProcessor(); // Rimosso
      // if (fileInputRef.current) { // Rimosso
      //   fileInputRef.current.value = ''; // Resetta l'input file // Rimosso
      // } // Rimosso
      setAttribution('');
      setSourceMinzoom('');
      setSourceMaxzoom('');
      // Reset del nuovo stato sourceLayerSelections
      setSourceLayerSelections([{ id: `default-reset-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' }]);
      setVectorBounds('');
      setVectorScheme('');
      setVectorTilesString('');
      setVectorTileSize('');
      setGeojsonBuffer('');
      setGeojsonTolerance('');
      setGeojsonCluster(false);
      setGeojsonClusterRadius('50');
      setGeojsonSourceMaxzoom('18');
      setGeojsonPromoteId('');
      setGeojsonGenerateId(false);
      setGeojsonClusterMaxZoom('14');
      setGeojsonClusterMinPoints('2');
      // Reset dei nuovi stati
      setSourceLayerOptions([{ value: '', label: 'Use a valid URL' }]);
      setIsLoadingSourceLayers(false);
      setSourceLayerError(null);
    }
  }, [isOpen]); // Rimosso resetFileProcessor dalle dipendenze

// Interfacce per PMTiles e TileJSON
interface PMTilesVectorLayer {
  id: string;
  fields?: Record<string, string | number | boolean>;
  description?: string;
  minzoom?: number;
  maxzoom?: number;
}

interface PMTilesMetadata {
  vector_layers?: PMTilesVectorLayer[];
  name?: string;
  description?: string;
  attribution?: string;
  format?: string;
  // Altre proprietà standard o personalizzate di PMTiles metadata
}

interface TileJSONVectorLayer {
  id: string;
  fields: Record<string, string>; // La specifica dice "textual descriptions"
  description?: string;
  minzoom?: number;
  maxzoom?: number;
}

interface TileJSONData {
  tilejson: string;
  name?: string;
  description?: string;
  version?: string;
  attribution?: string;
  template?: string;
  legend?: string;
  scheme?: 'xyz' | 'tms';
  tiles: string[];
  grids?: string[];
  data?: string[];
  minzoom?: number;
  maxzoom?: number;
  bounds?: [number, number, number, number];
  center?: [number, number, number];
  vector_layers?: TileJSONVectorLayer[];
}

// Definizione del payload per l'aggiunta di layer vettoriali
// const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => { // Rimosso
//   const file = event.target.files?.[0]; // Rimosso
//   if (file) { // Rimosso
//     processFile(file); // Rimosso
//   } else { // Rimosso
//     resetFileProcessor(); // Rimosso
//   } // Rimosso
// }; // Rimosso

const handleSubmit = async () => { // handleSubmit diventa async
    if (!layerName.trim()) {
      alert('Nome Layer è obbligatorio.');
      return;
    }

    const currentSourceType: 'geojson' | 'vector' = (urlSourceType === 'GeoJSON_URL') ? 'geojson' : 'vector';
    let finalGeoJsonUrlForMaplibre: string | undefined = undefined; // Per GeoJSON, MapLibre userà l'URL
    let calculatedBoundsForZoom: BBox | undefined = undefined; // Per i bounds calcolati

    // Validazioni URL
    if (urlSourceType === 'GeoJSON_URL' && !sourceUrl.trim()) {
      alert('URL Sorgente GeoJSON è obbligatorio.');
      return;
    }
    if (urlSourceType === 'TileJSON' && !sourceUrl.trim()) {
      alert('URL Sorgente TileJSON è obbligatorio.');
      return;
    }
    if (urlSourceType === 'PMTiles_URL' && !sourceUrl.trim()) {
      alert('URL Sorgente PMTiles è obbligatorio.');
      return;
    }
    // Validazione per PMTiles_URL e TileJSON (considerando la selezione multipla)
    if ((urlSourceType === 'PMTiles_URL' || urlSourceType === 'TileJSON')) {
      if (sourceLayerSelections.some(sel => !sel.sourceLayerName.trim())) {
        alert('Tutti i "Source Layer" selezionati sono obbligatori per PMTiles e TileJSON.');
        return;
      }
    }
    // Validazione per XYZ_VectorTiles
    if (urlSourceType === 'XYZ_VectorTiles') {
      if (!sourceUrl.trim() && !vectorTilesString.trim()) { // Controllo URL/Tiles string
        alert('URL Template XYZ o Lista URLs XYZ è obbligatoria.');
        return;
      }
      // La validazione per sourceLayerName vuoto per XYZ è gestita più avanti, prima del loop di creazione payload.
    }

    if (urlSourceType === 'GeoJSON_URL') {
      setIsLoadingGeoJson(true);
      finalGeoJsonUrlForMaplibre = sourceUrl.trim(); // MapLibre userà questo URL
      try {
        // console.log(`[AddVectorLayerDialog] Fetching GeoJSON from URL: ${finalGeoJsonUrlForMaplibre}`);
        const response = await fetch(finalGeoJsonUrlForMaplibre);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const geojsonData = await response.json();

        if (geojsonData && (geojsonData.type === 'FeatureCollection' || geojsonData.type === 'Feature' || geojsonData.type === 'GeometryCollection')) {
          calculatedBoundsForZoom = bbox(geojsonData) as BBox;
          // console.log('[AddVectorLayerDialog] Bounds calcolati per GeoJSON da URL:', calculatedBoundsForZoom);
        } else {
          // console.warn('[AddVectorLayerDialog] Dati GeoJSON da URL non validi, vuoti o tipo non supportato per bbox.');
          alert('I dati GeoJSON recuperati dall\'URL non sono validi o sono vuoti. Impossibile calcolare l\'estensione.');
        }
      } catch (error) {
        // console.error("Errore durante il fetch o il parsing del GeoJSON da URL:", error);
        alert(`Errore nel caricare GeoJSON da URL: ${error instanceof Error ? error.message : String(error)}. Il layer verrà aggiunto senza zoom automatico all'estensione.`);
        calculatedBoundsForZoom = undefined;
      } finally {
        setIsLoadingGeoJson(false);
      }
    } else if (urlSourceType === 'PMTiles_URL') {
      setIsLoadingPMTilesMeta(true);
      const pmtilesUrl = sourceUrl.trim();
      // L'URL per MapLibre deve essere l'URL HTTP/HTTPS originale, non quello con pmtiles://
      // Il protocollo pmtiles:// viene gestito internamente da MapLibre quando si registra l'istanza PMTiles.
      // Tuttavia, per la sourceSpecification.url, MapLibre si aspetta pmtiles:// seguito dall'URL HTTP(S).
      let finalPmtilesUrlForMaplibreSpec = pmtilesUrl;
      if (!pmtilesUrl.startsWith('http://') && !pmtilesUrl.startsWith('https://')) {
        // Se l'utente omette http(s)://, lo aggiungiamo per creare un URL valido per il fetch dei metadati
        // e per il protocollo pmtiles:// di MapLibre.
        finalPmtilesUrlForMaplibreSpec = `https://${pmtilesUrl}`;
      }
      
      // Aggiungi pmtiles:// se non già presente, per la sourceSpec di MapLibre
      if (!finalPmtilesUrlForMaplibreSpec.startsWith('pmtiles://')) {
          if (finalPmtilesUrlForMaplibreSpec.startsWith('https://')) {
            finalPmtilesUrlForMaplibreSpec = `pmtiles://${finalPmtilesUrlForMaplibreSpec}`;
          } else if (finalPmtilesUrlForMaplibreSpec.startsWith('http://')) {
             finalPmtilesUrlForMaplibreSpec = `pmtiles://${finalPmtilesUrlForMaplibreSpec}`;
          } else {
            // Caso in cui l'URL originale non aveva http/https e l'abbiamo aggiunto sopra,
            // ora aggiungiamo pmtiles://
            finalPmtilesUrlForMaplibreSpec = `pmtiles://https://${pmtilesUrl}`; // Usa l'URL originale per evitare https://https://
          }
      }


      try {
        // console.log(`[AddVectorLayerDialog] Initializing PMTiles from URL: ${pmtilesUrl}`);
        // PMTiles constructor accetta l'URL HTTP/S diretto
        const p = new PMTiles(pmtilesUrl.startsWith('pmtiles://') ? pmtilesUrl.substring('pmtiles://'.length) : pmtilesUrl);
        const header = await p.getHeader();
        if (header.minLon !== undefined && header.minLat !== undefined && header.maxLon !== undefined && header.maxLat !== undefined) {
          calculatedBoundsForZoom = [header.minLon, header.minLat, header.maxLon, header.maxLat];
          // console.log('[AddVectorLayerDialog] Bounds extracted from PMTiles header:', calculatedBoundsForZoom);
        } else {
          // console.warn('[AddVectorLayerDialog] Could not extract valid bounds from PMTiles header.');
          alert('Metadati PMTiles non contengono bounds validi. Lo zoom automatico potrebbe non funzionare.');
        }
      } catch (error) {
        // console.error("Errore durante l'inizializzazione o la lettura dell'header PMTiles:", error);
        alert(`Errore nell'elaborare PMTiles: ${error instanceof Error ? error.message : String(error)}. Il layer verrà aggiunto senza zoom automatico all'estensione.`);
        calculatedBoundsForZoom = undefined;
      } finally {
        setIsLoadingPMTilesMeta(false);
      }
    }
    // TODO: Aggiungere logica simile per TileJSON se si decide di estrarre bounds da lì.
    
    let sourceSpec: SourceSpecificationUnion;
    const sAttribution = attribution.trim() || undefined;
    const sMinzoomNum = sourceMinzoom !== '' ? parseInt(String(sourceMinzoom), 10) : undefined;
    const sMaxzoomNum = sourceMaxzoom !== '' ? parseInt(String(sourceMaxzoom), 10) : undefined;

    if ((sourceMinzoom !== '' && isNaN(sMinzoomNum as number)) || (sourceMaxzoom !== '' && isNaN(sMaxzoomNum as number))) {
        alert('Minzoom/Maxzoom della sorgente devono essere numeri validi.');
        return;
    }

    if (currentSourceType === 'vector') {
      let spec: VectorSourceSpecification;
      if (urlSourceType === 'PMTiles_URL') {
        // Ricostruisci finalPmtilesUrlForMaplibreSpec per la sourceSpecification
        // basandoci sull'input originale dell'utente per coerenza.
        let pmtilesUrlForSpec = sourceUrl.trim();
         if (!pmtilesUrlForSpec.startsWith('pmtiles://')) {
            if (pmtilesUrlForSpec.startsWith('http://') || pmtilesUrlForSpec.startsWith('https://')) {
                pmtilesUrlForSpec = `pmtiles://${pmtilesUrlForSpec}`;
            } else {
                // Se l'utente ha inserito solo example.com/file.pmtiles
                pmtilesUrlForSpec = `pmtiles://https://${pmtilesUrlForSpec}`;
            }
        }
        // A questo punto, pmtilesUrlForSpec dovrebbe essere pmtiles://PROTOCOL://URL

        spec = {
          type: 'vector',
          url: pmtilesUrlForSpec, // Usa l'URL formattato per MapLibre
          attribution: sAttribution,
          minzoom: sMinzoomNum,
          maxzoom: sMaxzoomNum,
          // scheme e tileSize non sono direttamente applicabili a PMTiles a livello di source spec
        };
      } else { // TileJSON or XYZ_VectorTiles
        spec = {
          type: 'vector',
          attribution: sAttribution,
          minzoom: sMinzoomNum,
          maxzoom: sMaxzoomNum,
          ...(vectorScheme && { scheme: vectorScheme as 'xyz' | 'tms' }), // Gestisce '' come assenza di scheme
        };
        if (urlSourceType === 'TileJSON') {
          spec.url = sourceUrl.trim();
        } else { // XYZ_VectorTiles
          if (vectorTilesString.trim()) {
              spec.tiles = vectorTilesString.split(/[\n,]+/).map(t => t.trim()).filter(t => t);
          } else if (sourceUrl.trim()) {
              spec.tiles = [sourceUrl.trim()];
          }
          if (vectorTileSize) { // Aggiungi solo se è stato selezionato un valore
            const vTileSizeNum = parseInt(vectorTileSize, 10);
            spec.tileSize = vTileSizeNum;
          }
        }
      }

      // Bounds sono applicabili a tutti i tipi di sorgente vettoriale (inclusi PMTiles)
      // Se stiamo aggiungendo un GeoJSON da URL, calculatedBoundsForZoom avrà la precedenza
      // sui bounds inseriti manualmente nel campo vectorBounds (che non dovrebbe essere visibile per GeoJSON_URL).
      if (urlSourceType !== 'GeoJSON_URL' && vectorBounds.trim()) {
        const boundsParts = vectorBounds.split(',').map(s => parseFloat(s.trim()));
        if (boundsParts.length === 4 && boundsParts.every(p => !isNaN(p))) {
          // Per TileJSON, XYZ, PMTiles, i bounds manuali vanno in spec.bounds
          // e anche in calculatedBoundsForZoom per lo zoom iniziale se non sovrascritti da logiche specifiche (es. fetch TileJSON)
          spec.bounds = boundsParts as BBox;
          if (!calculatedBoundsForZoom) calculatedBoundsForZoom = boundsParts as BBox;
        } else {
          alert('Formato Bounds non valido. Usare: minLng,minLat,maxLng,maxLat');
          return;
        }
      }
      // TODO: Per TileJSON, si potrebbe fare un fetch qui per estrarre i bounds dal JSON
      // e popolare calculatedBoundsForZoom, sovrascrivendo quelli manuali.
      // Per PMTiles, la libreria pmtiles potrebbe estrarre i bounds.
      sourceSpec = spec;
    } else { // currentSourceType === 'geojson'
      const spec: GeoJSONSourceSpecification = {
        type: 'geojson',
        data: finalGeoJsonUrlForMaplibre as GeoJSONSourceData, // Usa l'URL per MapLibre
        attribution: sAttribution,
        maxzoom: sMaxzoomNum, // Questo è il maxzoom generale della sorgente, se fornito
      };
      // Nota: GeoJSONSourceSpecification non ha 'bounds'. I bounds calcolati
      // verranno passati separatamente nel payload a addVectorSourceAndLayer.
      
      // Questo maxzoom è specificamente per il tiling della sorgente GeoJSON, diverso dal maxzoom generale sopra.
      // MapLibre chiama questa proprietà 'maxzoom' nella GeoJSONSourceSpecification.
      const gSourceTilingMaxzoomNum = geojsonSourceMaxzoom !== '' ? parseInt(String(geojsonSourceMaxzoom), 10) : undefined;
      if (gSourceTilingMaxzoomNum !== undefined && isNaN(gSourceTilingMaxzoomNum)) {
        alert('GeoJSON Maxzoom per tiling deve essere un numero valido.');
        return;
      }
      // Sovrascrive il maxzoom generale se gSourceTilingMaxzoomNum è specificato,
      // altrimenti usa il maxzoom comune (sMaxzoomNum) se fornito.
      // Se entrambi sono undefined, spec.maxzoom rimane undefined.
      if (gSourceTilingMaxzoomNum !== undefined) {
        spec.maxzoom = gSourceTilingMaxzoomNum;
      }
      // Se sMaxzoomNum è definito e gSourceTilingMaxzoomNum no, spec.maxzoom è già sMaxzoomNum.
      // Se nessuno dei due è definito, spec.maxzoom è undefined.
      // Se gSourceTilingMaxzoomNum è definito, ha la precedenza per la prop 'maxzoom' della sorgente GeoJSON.


      // const gMaxzoomNum = geojsonSourceMaxzoom !== '' ? parseInt(String(geojsonSourceMaxzoom), 10) : undefined; // Vecchia riga
      // if (gMaxzoomNum !== undefined && isNaN(gMaxzoomNum)) { // Vecchio controllo
      //   alert('GeoJSON Maxzoom per tiling deve essere un numero valido.');
      //   return;
      // }
      // spec.maxzoom = gMaxzoomNum; // Vecchia assegnazione, ora gestita sopra


      const gBufferNum = geojsonBuffer !== '' ? parseInt(String(geojsonBuffer), 10) : undefined;
      if (gBufferNum !== undefined && (isNaN(gBufferNum) || gBufferNum < 0)) {
        alert('GeoJSON Buffer deve essere un numero non negativo.');
        return;
      }
      spec.buffer = gBufferNum;

      const gToleranceNum = geojsonTolerance !== '' ? parseFloat(String(geojsonTolerance)) : undefined;
      if (gToleranceNum !== undefined && (isNaN(gToleranceNum) || gToleranceNum < 0)) {
        alert('GeoJSON Tolerance deve essere un numero non negativo.');
        return;
      }
      spec.tolerance = gToleranceNum;

      spec.cluster = geojsonCluster;
      if (geojsonCluster) {
        const gClusterRadiusNum = geojsonClusterRadius !== '' ? parseInt(String(geojsonClusterRadius), 10) : undefined;
        if (gClusterRadiusNum !== undefined && (isNaN(gClusterRadiusNum) || gClusterRadiusNum < 0)) {
          alert('GeoJSON Cluster Radius deve essere un numero non negativo.');
          return;
        }
        spec.clusterRadius = gClusterRadiusNum;

        const gClusterMaxZoomNum = geojsonClusterMaxZoom !== '' ? parseInt(String(geojsonClusterMaxZoom), 10) : undefined;
        if (gClusterMaxZoomNum !== undefined && (isNaN(gClusterMaxZoomNum) || gClusterMaxZoomNum < 0)) {
          alert('GeoJSON Cluster MaxZoom deve essere un numero non negativo.');
          return;
        }
        spec.clusterMaxZoom = gClusterMaxZoomNum;

        const gClusterMinPointsNum = geojsonClusterMinPoints !== '' ? parseInt(String(geojsonClusterMinPoints), 10) : undefined;
        if (gClusterMinPointsNum !== undefined && (isNaN(gClusterMinPointsNum) || gClusterMinPointsNum < 0)) {
          alert('GeoJSON Cluster Min Points deve essere un numero non negativo.');
          return;
        }
        spec.clusterMinPoints = gClusterMinPointsNum;
      }
      
      if (geojsonPromoteId.trim()) {
        spec.promoteId = geojsonPromoteId.trim();
      }
      spec.generateId = geojsonGenerateId;
      sourceSpec = spec;
    }

    // Controlli di sanità per GeoJSONSourceSpecification
    if (currentSourceType === 'geojson' && sourceSpec) {
      const geoSpec = sourceSpec as GeoJSONSourceSpecification;
      if (geoSpec.type !== 'geojson') {
        console.error("Errore di asserzione interna: sourceSpecification.type per GeoJSON non è 'geojson'.", geoSpec);
        alert("Errore interno: tipo sorgente GeoJSON errato.");
        return;
      }
      if (!geoSpec.data) { // Controlla che data esista
        console.error("Errore di asserzione interna: sourceSpecification.data per GeoJSON è mancante.", geoSpec);
        alert("Errore interno: dati sorgente GeoJSON mancanti.");
        return;
      }
      // Solo URL mode, quindi geoSpec.data deve essere una stringa URL
      if (typeof geoSpec.data !== 'string' || geoSpec.data.length === 0) {
        console.error("Errore di asserzione interna: sourceSpecification.data per GeoJSON URL non è una stringa valida o è vuota.", geoSpec);
        alert("Errore interno: URL dati GeoJSON mancante o errato.");
        return;
      }
      // Rimosso il controllo per uploadMode === 'file'
    }
    
    try {
      if (!addVectorSourceAndLayer) {
        alert("La funzionalità 'addVectorSourceAndLayer' non è ancora implementata nello store.");
        console.error("Errore: addVectorSourceAndLayer non è definita. Implementarla in layerSlice.ts");
        return;
      }

      if (urlSourceType === 'PMTiles_URL' || urlSourceType === 'TileJSON' || urlSourceType === 'XYZ_VectorTiles') {
        // Validazione specifica per XYZ_VectorTiles: sourceLayerName non deve essere vuoto
        if (urlSourceType === 'XYZ_VectorTiles') {
          if (sourceLayerSelections.some(sel => !sel.sourceLayerName.trim())) {
            alert('Layer name is required for all selected XYZ layers.');
            return;
          }
        }
        // La validazione per PMTiles e TileJSON (sourceLayerName non vuoto) è già globale.

        if (sourceLayerSelections.length === 0) {
          alert('Nessun layer sorgente definito. Aggiungerne almeno uno.');
          return;
        }

        sourceLayerSelections.forEach((selection, index) => {
          const payloadId = `${currentSourceType}-${layerName.replace(/\s+/g, '_').toLowerCase()}-${(selection.sourceLayerName.trim().replace(/\s+/g, '_').toLowerCase() || 'layer')}-${Date.now().toString()}-${index}`;
          
          const payload: AddVectorLayerPayload = {
            id: payloadId,
            name: layerName.trim(),
            sourceType: currentSourceType, // 'vector' per PMTiles/TileJSON/XYZ
            maplibreLayerType: selection.maplibreLayerType,
            sourceLayer: selection.sourceLayerName.trim() || undefined, // Specifico per questa selezione
            sourceSpecification: sourceSpec, // Comune per tutti i layer da questa sorgente URL
            calculatedBoundsForZoom: calculatedBoundsForZoom, // Comune
          };
          addVectorSourceAndLayer(payload);
        });
      } else { // Solo GeoJSON_URL
        const firstSelection = sourceLayerSelections[0]; // Dovrebbe esserci sempre almeno un elemento di default
        const finalMaplibreLayerType = firstSelection?.maplibreLayerType || 'circle';
        // Per GeoJSON_URL, sourceLayer non è applicabile a livello di payload principale.

        const payloadId = `${currentSourceType}-${layerName.replace(/\s+/g, '_').toLowerCase()}-${Date.now().toString()}`;

        const payload: AddVectorLayerPayload = {
          id: payloadId,
          name: layerName.trim(),
          sourceType: currentSourceType, // 'geojson'
          maplibreLayerType: finalMaplibreLayerType,
          // sourceLayer è undefined per GeoJSON
          sourceSpecification: sourceSpec,
          calculatedBoundsForZoom: calculatedBoundsForZoom,
        };
        addVectorSourceAndLayer(payload);
      }
      
      onClose(); // Chiamato solo se tutte le operazioni di addVectorSourceAndLayer hanno successo
    } catch (error) {
      console.error("Errore durante l'aggiunta del layer:", error);
      alert(`Errore durante l'aggiunta del layer: ${error instanceof Error ? error.message : String(error)}`);
      // onClose() non viene chiamato qui, come da specifiche, se un errore interrompe il processo.
    }
  };

  useEffect(() => {
    if (!isOpen) {
      // Se il dialogo non è aperto, assicurati che il timeout sia cancellato
      // e resetta gli stati relativi al caricamento dei layer sorgente.
      // Questo è particolarmente utile se il dialogo viene chiuso mentre un fetch è in timeout.
      // La pulizia del timeout avviene già nel return dell'effetto principale,
      // ma un reset esplicito qui può essere utile per coerenza.
      setIsLoadingSourceLayers(false);
      setSourceLayerError(null);
      setSourceLayerOptions([{ value: '', label: 'Use a valid URL' }]);
      // Reset sourceLayerSelections a una singola entry di default quando il dialogo non è aperto o l'URL cambia
      setSourceLayerSelections([{ id: `default-closed-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' }]);
      return;
    }

    const fetchSourceLayers = async () => {
      if ((urlSourceType !== 'PMTiles_URL' && urlSourceType !== 'TileJSON') || !sourceUrl.trim()) {
        setSourceLayerOptions([{ value: '', label: 'Use a valid URL' }]);
        setIsLoadingSourceLayers(false);
        setSourceLayerError(null);
        // Resetta sourceLayerSelections a una singola entry di default
        setSourceLayerSelections([{ id: `default-url-invalid-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' }]);
        return;
      }

      setIsLoadingSourceLayers(true);
      setSourceLayerError(null);
      setSourceLayerOptions([{ value: '', label: 'Caricamento layers...' }]);
      // Resetta sourceLayerSelections a una singola entry di default all'inizio del fetch
      setSourceLayerSelections([{ id: `default-fetch-start-${Date.now()}`, sourceLayerName: '', maplibreLayerType: 'circle' }]);
      
      try {
        let layers: { id: string, description?: string, fields?: Record<string, string | number | boolean> }[] = [];
        if (urlSourceType === 'PMTiles_URL') {
          const pmtUrl = sourceUrl.trim().startsWith('pmtiles://')
            ? sourceUrl.trim().substring('pmtiles://'.length)
            : sourceUrl.trim();
          const p = new PMTiles(pmtUrl);
          const metadata = await p.getMetadata() as PMTilesMetadata;
          if (metadata && metadata.vector_layers && metadata.vector_layers.length > 0) {
            layers = metadata.vector_layers;
          } else {
            setSourceLayerError('Nessun vector_layers trovato nei metadati PMTiles.');
            setSourceLayerOptions([{ value: '', label: 'Nessun layer PMTiles trovato.' }]);
          }
        } else if (urlSourceType === 'TileJSON') {
          const response = await fetch(sourceUrl.trim());
          if (!response.ok) {
            throw new Error(`Errore HTTP: ${response.status}`);
          }
          const tileJsonData = await response.json() as TileJSONData;
          if (tileJsonData && tileJsonData.vector_layers && tileJsonData.vector_layers.length > 0) {
            layers = tileJsonData.vector_layers;
          } else {
            setSourceLayerError('Nessun vector_layers trovato nel TileJSON.');
            setSourceLayerOptions([{ value: '', label: 'Nessun layer TileJSON trovato.' }]);
          }
        }

        if (layers.length > 0) {
          setSourceLayerOptions([
            { value: '', label: 'Seleziona un layer sorgente...' },
            ...layers.map(layer => ({ value: layer.id, label: `${layer.id}${layer.description ? ` (${layer.description})` : ''}` })),
          ]);
        } else if (!sourceLayerError) {
          // Se non ci sono layer ma nessun errore precedente (es. PMTiles/TileJSON validi ma senza vector_layers)
          // Il messaggio è già stato impostato sopra (es. 'Nessun layer PMTiles trovato.')
          // setSourceLayerOptions([{ value: '', label: 'Nessun layer sorgente trovato.' }]);
        }
      } catch (error) {
        console.error('Errore nel recuperare i source layers:', error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        setSourceLayerError(`Errore recupero layer: ${errorMessage}`);
        setSourceLayerOptions([{ value: '', label: 'Errore caricamento layer.' }]);
      } finally {
        setIsLoadingSourceLayers(false);
      }
    };

    const timerId = setTimeout(() => {
      if (isOpen) {
        fetchSourceLayers();
      }
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [sourceUrl, urlSourceType, isOpen]);

  if (!isOpen) {
    return null;
  }

  const urlSourceTypeOptions = [
    { value: 'GeoJSON_URL', label: 'GeoJSON URL' },
    { value: 'TileJSON', label: 'TileJSON URL' },
    { value: 'XYZ_VectorTiles', label: 'XYZ Vector Tiles (PBF)' },
    { value: 'PMTiles_URL', label: 'PMTiles URL' },
  ];

  // const uploadModeOptions = [ // Rimosso
  //   { value: 'url', label: 'Da URL' }, // Rimosso
  //   { value: 'file', label: 'Da File Locale' }, // Rimosso
  // ]; // Rimosso

  const maplibreLayerTypeOptions = [
    { value: 'fill', label: 'Fill' },
    { value: 'line', label: 'Line' },
    { value: 'circle', label: 'Circle' },
    { value: 'symbol', label: 'Symbol' },
    { value: 'heatmap', label: 'Heatmap' },
  ];

  const schemeOptions = [
    { value: '', label: 'Seleziona schema (opzionale)' }, // NUOVA OPZIONE
    { value: 'xyz', label: 'xyz' },
    { value: 'tms', label: 'tms' },
  ];

const tileSizeOptions = [
  { value: '', label: 'Seleziona dimensione tile (opzionale)' }, // NUOVA OPZIONE
  { value: '128', label: '128px' },
  { value: '256', label: '256px' },
  { value: '512', label: '512px' },
];

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => {
    if (typeof event === 'string') {
      setter(event);
    } else {
      setter(event.target.value);
    }
  };

  const handleNumberInputChange = (setter: React.Dispatch<React.SetStateAction<number | string>>) => (value: number | string | undefined) => {
    setter(value ?? '');
  };


  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 'var(--z-index-overlay)',
    }}>
      <div style={{
        backgroundColor: 'var(--color-background-ui)',
        padding: '20px', // Modificato padding
        borderRadius: 'var(--border-radius-large, 8px)', // Modificato borderRadius
        boxShadow: 'var(--shadow-elevation-high, 0 8px 16px rgba(0,0,0,0.2))', // Modificato boxShadow
        width: 'clamp(400px, 50vw, 600px)', // Modificato width
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
        color: 'var(--color-text-primary)', // Aggiunto color
        zIndex: 'var(--z-index-dialog)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xlarge, 1.5rem)', color: 'var(--color-text-primary)', flexGrow: 1, textAlign: 'center' }}>
            Add Vector Layer from URL
          </h2>
          <button
            onClick={onClose}
            aria-label="Chiudi dialogo"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-secondary)',
              fontSize: '1.8rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1',
            }}
          >
            &times;
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div style={{
            flexGrow: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px', // Coerente con AddRasterLayerDialog
            paddingRight: '10px', // Per scrollbar, come in AddRasterLayerDialog
            // maxHeight rimosso, maxHeight del contenitore principale e flexShrink su header/footer gestiscono l'altezza
        }}>
          
          {/* Riga 1: Source URL Format Select e Item Name Input */}
          <div className={styles.twoItemsRow}>
            <div className={styles.inputWrapperFlexGrow1}> {/* Per Source URL Format */}
              <label htmlFor="vectorUrlSourceTypeSelect" className="input-label">Source URL format:</label>
              <SelectInput
                id="vectorUrlSourceTypeSelect"
                value={urlSourceType}
                options={urlSourceTypeOptions}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setUrlSourceType(event.target.value as VectorUrlSourceType)}
                className={styles.fullWidthSelect}
              />
            </div>
            <div className={styles.inputWrapperFlexGrow1}> {/* Per Item Name */}
              <ExtendedTextInput
                id="vectorLayerName"
                name="vectorLayerName"
                label="Name:" // Label come prop
                value={layerName}
                onChange={handleInputChange(setLayerName)}
                placeholder="my_vector"
              />
            </div>
          </div>

          {/* Riga 2: URL Input */}
          <div>
            <UrlInput
              id="vectorSourceUrl"
              label={ // Label come prop
                urlSourceType === 'GeoJSON_URL' ? "GeoJSON URL:" :
                urlSourceType === 'TileJSON' ? "TileJSON URL:" :
                urlSourceType === 'XYZ_VectorTiles' ? "URL Template XYZ (es. https://.../{z}/{x}/{y}.pbf):" :
                "PMTiles URL:"
              }
              value={sourceUrl}
              onChange={handleInputChange(setSourceUrl)}
              placeholder={
                urlSourceType === 'GeoJSON_URL' ? "https://example.com/data.geojson" :
                urlSourceType === 'TileJSON' ? "https://example.com/tiles.json" :
                urlSourceType === 'PMTiles_URL' ? "https://example.com/data.pmtiles" :
                "https://{s}.tile.server.com/{z}/{x}/{y}.pbf"
              }
            />
          </div>


          {/* Sezione per la selezione multipla di Source Layer e MapLibre Layer Type */}
          {(urlSourceType === 'PMTiles_URL' || urlSourceType === 'TileJSON' || urlSourceType === 'XYZ_VectorTiles') ? (
            sourceLayerSelections.map((selection, index) => (
              <div key={selection.id} className={styles.layerSelectionRowContainer}>
                {index === 0 && (
                  <div className={styles.labelsRow}>
                    <div className={styles.actionButtonsPlaceholder}></div>
                    <div className={styles.inputWrapperFlexGrow1}>
                      <label htmlFor={urlSourceType === 'XYZ_VectorTiles' ? `xyzSourceLayerNameText-${selection.id}` : `sourceLayerNameSelect-${selection.id}`} className="input-label">
                        {urlSourceType === 'XYZ_VectorTiles' ? 'Layer name (required):' : 'Source Layer (required):'}
                      </label>
                    </div>
                    <div className={styles.inputWrapperFlexGrow1}>
                      <label htmlFor={`maplibreLayerTypeSelect-${selection.id}`} className="input-label">
                        Choose Layer Type:
                      </label>
                    </div>
                  </div>
                )}
                <div className={styles.layerSelectionRowItems}>
                  <div className={styles.actionButtonsWrapper}>
                    <button
                      type="button"
                      onClick={addSourceLayerSelection}
                      className={styles.iconButton}
                      aria-label="Add new source layer selection"
                      disabled={
                        (urlSourceType === 'PMTiles_URL' || urlSourceType === 'TileJSON')
                          ? (isLoadingSourceLayers || !sourceUrl.trim())
                          : (urlSourceType === 'XYZ_VectorTiles'
                            ? !sourceUrl.trim() // Per XYZ, disabilita solo se l'URL sorgente è vuoto
                            : false) 
                      }
                    >
                      <Plus size={18} />
                    </button>
                    {sourceLayerSelections.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSourceLayerSelection(selection.id)}
                        className={styles.iconButton}
                        aria-label={`Remove source layer selection ${index + 1}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  
                  <div className={styles.inputWrapperFlexGrow1}>
                    {index > 0 && (
                      <label htmlFor={urlSourceType === 'XYZ_VectorTiles' ? `xyzSourceLayerNameText-${selection.id}` : `sourceLayerNameSelect-${selection.id}`} className={styles.srOnly}>
                        {urlSourceType === 'XYZ_VectorTiles' ? 'Layer name (required):' : 'Source Layer (required):'}
                      </label>
                    )}
                    {urlSourceType === 'XYZ_VectorTiles' ? (
                      <ExtendedTextInput
                        id={`xyzSourceLayerNameText-${selection.id}`}
                        name={`xyzSourceLayerNameText-${selection.id}`}
                        value={selection.sourceLayerName}
                        onChange={(e) => {
                          const value = typeof e === 'string' ? e : (e.target as HTMLInputElement).value;
                          updateSourceLayerSelection(selection.id, 'sourceLayerName', value);
                        }}
                        placeholder="Es. 'roads' o 'buildings'"
                        className={styles.fullWidthSelect} 
                      />
                    ) : ( // PMTiles_URL or TileJSON
                      <SelectInput
                        id={`sourceLayerNameSelect-${selection.id}`}
                        value={selection.sourceLayerName}
                        options={sourceLayerOptions}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                          updateSourceLayerSelection(selection.id, 'sourceLayerName', event.target.value)
                        }
                        className={styles.fullWidthSelect}
                        disabled={
                          isLoadingSourceLayers ||
                          !sourceUrl.trim() ||
                          (sourceLayerOptions.length === 1 && sourceLayerOptions[0].value === '' && !sourceLayerError && !isLoadingSourceLayers)
                        }
                      />
                    )}
                  </div>
                  
                  <div className={styles.inputWrapperFlexGrow1}>
                    {index > 0 && (
                      <label htmlFor={`maplibreLayerTypeSelect-${selection.id}`} className={styles.srOnly}>
                        Choose Layer Type:
                      </label>
                    )}
                    <SelectInput
                      id={`maplibreLayerTypeSelect-${selection.id}`}
                      value={selection.maplibreLayerType}
                      options={maplibreLayerTypeOptions}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                        updateSourceLayerSelection(selection.id, 'maplibreLayerType', event.target.value as LayerBase['maplibreLayerType'])
                      }
                      className={styles.fullWidthSelect}
                    />
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Caso solo per GeoJSON_URL (singola selezione per maplibreLayerType)
            <div className={styles.twoItemsRow}>
              <div className={styles.inputWrapperFlexGrow1}>
                <label htmlFor="vectorMaplibreLayerType" className="input-label">Choose Layer Type:</label>
                <SelectInput
                  id="vectorMaplibreLayerType"
                  value={sourceLayerSelections[0]?.maplibreLayerType || 'circle'} 
                  options={maplibreLayerTypeOptions}
                  onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                    if (sourceLayerSelections.length > 0) {
                      updateSourceLayerSelection(sourceLayerSelections[0].id, 'maplibreLayerType', event.target.value as LayerBase['maplibreLayerType']);
                    } else { 
                      setSourceLayerSelections([{ id: `default-single-geojson-${Date.now()}`, sourceLayerName: '', maplibreLayerType: event.target.value as LayerBase['maplibreLayerType'] }]);
                    }
                  }}
                  className={styles.fullWidthSelect}
                />
              </div>
              {/* Per GeoJSON_URL, non c'è un input per sourceLayerName qui */}
            </div>
          )}
 
          {/* Messaggi di caricamento/errore per source layers (mostrati una volta sotto le selezioni, solo per PMTiles/TileJSON) */}
          {(urlSourceType === 'PMTiles_URL' || urlSourceType === 'TileJSON') && (
            <>
              {isLoadingSourceLayers && <div className={styles.loadingMessage}>Caricamento layers...</div>}
              {sourceLayerError && !isLoadingSourceLayers && <div className={styles.errorMessage}>{sourceLayerError}</div>}
              {!isLoadingSourceLayers && !sourceLayerError && sourceUrl.trim() && sourceLayerOptions.length === 1 &&
               (sourceLayerOptions[0].label.includes('Nessun layer') || sourceLayerOptions[0].label.includes('Errore caricamento')) && (
                <div className={styles.infoMessage}>{sourceLayerOptions[0].label}. Controlla l'URL o il formato.</div>
              )}
            </>
          )}

          {/* Opzioni Comuni */}
          <div className={styles.formSection}>
            <ExtendedTextInput
              id="commonAttribution"
              name="commonAttribution"
              label="Attribution (optional):" // Label come prop
              value={attribution}
              onChange={handleInputChange(setAttribution)}
              placeholder="Es. © OpenStreetMap contributors"
            />
            <div className={styles.twoItemsRow}>
              {urlSourceType !== 'GeoJSON_URL' && (
                <div className={styles.inputWrapperFlexGrow1}>
                  <label htmlFor="commonSourceMinzoom" className="input-label">Source MinZoom (Opt.):</label>
                  <NumberInput
                    id="commonSourceMinzoom"
                    value={sourceMinzoom}
                    onChange={handleNumberInputChange(setSourceMinzoom)}
                    placeholder="Es. 0"
                    min={0} max={24} step={1}
                  />
                </div>
              )}
              <div className={styles.inputWrapperFlexGrow1}>
                <label htmlFor="commonSourceMaxzoom" className="input-label">Source MaxZoom (Opt.):</label>
                <NumberInput
                  id="commonSourceMaxzoom"
                  value={sourceMaxzoom}
                  onChange={handleNumberInputChange(setSourceMaxzoom)}
                  placeholder="Es. 22"
                  min={0} max={24} step={1}
                />
              </div>
            </div>
          </div>


          {/* Opzioni specifiche per GeoJSON (solo URL) */}
          { urlSourceType === 'GeoJSON_URL' && (
            <div className={styles.formSection}>
              <div className={styles.twoItemsRow}>
                <div className={styles.inputWrapperFlexGrow1}>
                    <label htmlFor="geojsonBuffer" className="input-label">Buffer (Opt.):</label>
                    <NumberInput id="geojsonBuffer" value={geojsonBuffer} onChange={handleNumberInputChange(setGeojsonBuffer)} placeholder="" min={0} />
                </div>
                <div className={styles.inputWrapperFlexGrow1}>
                    <label htmlFor="geojsonTolerance" className="input-label">Tolerance (Opt.):</label>
                    <NumberInput id="geojsonTolerance" value={geojsonTolerance} onChange={handleNumberInputChange(setGeojsonTolerance)} placeholder="" step={0.001} min={0} />
                </div>
              </div>
              <div>
                <label htmlFor="geojsonSourceMaxzoomSpecific" className="input-label">Maxzoom Tiling GeoJSON (Opt., def 18):</label>
                <NumberInput id="geojsonSourceMaxzoomSpecific" value={geojsonSourceMaxzoom} onChange={handleNumberInputChange(setGeojsonSourceMaxzoom)} placeholder="18" min={0} max={24} step={1} />
              </div>
              {sourceLayerSelections[0]?.maplibreLayerType === 'circle' && (
                <>
                  <ToggleButton
                    id="geojsonCluster"
                    isOn={geojsonCluster}
                    onToggle={() => setGeojsonCluster(!geojsonCluster)}
                    label="Enable Clustering" // Label come prop
                  />
                  {geojsonCluster && (
                    <>
                      <div>
                        <label htmlFor="geojsonClusterRadius" className="input-label">Cluster Radius (Opt., def 50):</label>
                        <NumberInput id="geojsonClusterRadius" value={geojsonClusterRadius} onChange={handleNumberInputChange(setGeojsonClusterRadius)} placeholder="50" min={0} />
                      </div>
                      <div className={styles.twoItemsRow}>
                        <div className={styles.inputWrapperFlexGrow1}>
                          <label htmlFor="geojsonClusterMaxZoom" className="input-label">Cluster MaxZoom (Opt., def 14):</label>
                          <NumberInput id="geojsonClusterMaxZoom" value={geojsonClusterMaxZoom} onChange={handleNumberInputChange(setGeojsonClusterMaxZoom)} placeholder="14" min={0} max={24} step={1} />
                        </div>
                        <div className={styles.inputWrapperFlexGrow1}>
                          <label htmlFor="geojsonClusterMinPoints" className="input-label">Cluster Min Points (Opt., def 2):</label>
                          <NumberInput id="geojsonClusterMinPoints" value={geojsonClusterMinPoints} onChange={handleNumberInputChange(setGeojsonClusterMinPoints)} placeholder="2" min={0} step={1} />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
              <ExtendedTextInput
                id="geojsonPromoteId"
                name="geojsonPromoteId"
                label="Promote ID (Opt):" // Label come prop
                value={geojsonPromoteId}
                onChange={handleInputChange(setGeojsonPromoteId)}
                placeholder={`Es. 'id' o '{ "layerA": "propA" }'`}
              />
              <ToggleButton
                id="geojsonGenerateId"
                isOn={geojsonGenerateId}
                onToggle={() => setGeojsonGenerateId(!geojsonGenerateId)}
                label="Create ID Features" // Label come prop
              />
            </div>
          )}

          {/* Opzioni specifiche per Vector Tiles (TileJSON, XYZ o PMTiles) */}
          {(urlSourceType === 'TileJSON' || urlSourceType === 'XYZ_VectorTiles' || urlSourceType === 'PMTiles_URL') && (
            <div className={styles.formSection}>
              <ExtendedTextInput
                id="vectorBounds"
                name="vectorBounds"
                label="Bounds (Opt., minLng,minLat,maxLng,maxLat):" // Label come prop
                value={vectorBounds}
                onChange={handleInputChange(setVectorBounds)}
                placeholder="-180,-85,180,85"
              />
              
              {(urlSourceType === 'TileJSON' || urlSourceType === 'XYZ_VectorTiles') && (
                <div>
                  <label htmlFor="vectorScheme" className="input-label">Scheme (Opt.):</label>
                  <SelectInput
                      id="vectorScheme"
                      value={vectorScheme}
                      options={schemeOptions}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setVectorScheme(event.target.value as 'xyz' | 'tms')}
                      className={styles.fullWidthSelect}
                  />
                </div>
              )}
              
              {urlSourceType === 'XYZ_VectorTiles' && (
                <>
                  <div>
                    <label htmlFor="vectorTilesString" className="input-label">URLs list for XYZ tiles (Alternative to URL Template):</label>
                    <textarea
                      id="vectorTilesString"
                      value={vectorTilesString}
                      onChange={handleInputChange(setVectorTilesString)}
                      placeholder="https://tile1.../{z}/{x}/{y}.pbf,\nhttps://tile2.../{z}/{x}/{y}.pbf"
                      rows={3}
                      style={{
                        width: '100%',
                        padding: 'var(--spacing-sm)',
                        border: '1px solid var(--color-border-input)',
                        borderRadius: 'var(--border-radius-medium)',
                        boxSizing: 'border-box',
                        resize: 'vertical',
                        backgroundColor: 'var(--color-background-input)',
                        color: 'var(--color-text-input)',
                        fontFamily: 'inherit',
                        fontSize: 'var(--font-size-normal)',
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="vectorTileSizeSelect" className="input-label">Tile Size (px, Opt.):</label>
                    <SelectInput
                      id="vectorTileSizeSelect"
                      value={vectorTileSize}
                      options={tileSizeOptions}
                      onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setVectorTileSize(event.target.value)}
                      className={styles.fullWidthSelect}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer with Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid var(--color-border-subtle)', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              backgroundColor: 'var(--color-button-secondary)',
              color: 'var(--color-text-button)',
              border: '1px solid var(--color-border-button-secondary)',
              borderRadius: 'var(--border-radius-medium)',
              fontSize: 'var(--font-size-normal)', // Aggiunto fontSize come da AddRasterLayerDialog
              // fontWeight rimosso per coerenza con AddRasterLayerDialog
            }}>
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoadingGeoJson || isLoadingPMTilesMeta || isLoadingSourceLayers}
            style={{
              padding: '8px 16px',
              cursor: (isLoadingGeoJson || isLoadingPMTilesMeta || isLoadingSourceLayers) ? 'wait' : 'pointer',
              backgroundColor: (isLoadingGeoJson || isLoadingPMTilesMeta || isLoadingSourceLayers) ? 'var(--color-button-disabled)' : 'var(--color-button-primary)',
              color: 'var(--color-text-button-primary)',
              border: 'none',
              borderRadius: 'var(--border-radius-medium)',
              fontSize: 'var(--font-size-normal)',
            }}>
            {/* Testo del pulsante dinamico */}
            {isLoadingGeoJson ? 'Loading GeoJSON...'
              : isLoadingPMTilesMeta ? 'Loading PMTiles...'
              : isLoadingSourceLayers ? 'Loading Source Layers...'
              : 'Load Vector Layer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddVectorLayerDialog;